import type { ExtendedResource } from '@seen/core/ExtendedResource'
import { invariant } from '@seen/core/invariant'
import { type JSX, useCallback, useEffect, useMemo } from 'react'
import {
  FieldForm,
  type FieldFormProps,
  type FieldFormRender,
} from '#src/components/core/form/FieldForm.tsx'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import {
  type UsePatchMutationProxyOptions,
  usePatchMutation,
} from '#src/hooks/usePatchMutation.ts'

export interface ResourceFieldFormProps<
  TInstance extends ExtendedResource | undefined | null,
  TValue,
> extends Omit<
  FieldFormProps<ToFormValue<TValue, TInstance>>,
  'onSubmit' | 'defaultValue' | 'render'
> {
  instance: TInstance
  valueFn: (instance: NonNullable<TInstance>) => TValue
  patchFn: (
    instance: NonNullable<TInstance>,
    value: ToPatchValue<TValue>
  ) => any
  render: FieldFormRender<ToFormValue<TValue, TInstance>>
  mutationOptions?: UsePatchMutationProxyOptions<
    NonNullable<TInstance>
  >['mutation']
  debounceTyping?: boolean
  skipVersionCheck?: boolean
}

type ToPatchValue<TValue> = TValue extends undefined
  ? NonNullable<TValue> | null
  : TValue

type ToFormValue<TValue, TInstance> = TInstance extends undefined | null
  ? NonNullable<TValue> | null
  : TValue extends undefined
    ? NonNullable<TValue> | null
    : TValue

interface MutationScope {
  scope?: { id: string }
}

export function ResourceFieldForm<
  TInstance extends ExtendedResource | undefined | null,
  TValue,
>({
  instance,
  valueFn,
  patchFn,
  render,
  disabled,
  mutationOptions,
  debounceTyping,
  skipVersionCheck,
  ...delegated
}: ResourceFieldFormProps<TInstance, TValue>): JSX.Element {
  const referenceString = instance?.reference.reference

  const mutationScope: MutationScope = {}
  if (referenceString) {
    // We don't want multiple patch mutations to go out for a single resource at the same time. Mutations with the same scope id will run in serial.
    mutationScope.scope = { id: referenceString }
  }

  const { mutate } = usePatchMutation<NonNullable<TInstance>>({
    mutation: {
      ...mutationOptions,
      ...mutationScope,
    },
  })

  const defaultValue = useMemo(() => {
    const value = instance ? valueFn(instance) : undefined
    return value ?? null
  }, [instance, valueFn])

  const handleSubmit = useCallback(
    (value: ToPatchValue<TValue>) => {
      invariant(instance, 'The form should be disabled if instance is falsy')
      mutate({
        instance,
        mutateFn: (i) => patchFn(i, value),
        patchOptions: {
          skipVersionCheck,
        },
      })
    },
    [mutate, instance, patchFn, skipVersionCheck]
  )

  const isReadOnly = useIsReadOnly()
  const isDisabled = !instance || disabled || isReadOnly

  if (debounceTyping) {
    return (
      <DebouncedFieldForm
        referenceString={referenceString}
        defaultValue={defaultValue}
        onSubmit={handleSubmit as FieldFormProps<TValue>['onSubmit']}
        disabled={isDisabled}
        render={render as FieldFormProps<TValue>['render']}
        {...delegated}
      />
    )
  }

  return (
    <FieldForm
      defaultValue={defaultValue}
      onSubmit={handleSubmit as FieldFormProps<TValue>['onSubmit']}
      disabled={isDisabled}
      render={render as FieldFormProps<TValue>['render']}
      {...delegated}
    />
  )
}

function DebouncedFieldForm<TValue>({
  referenceString,
  render,
  onSubmit,
  ...delegated
}: FieldFormProps<TValue> & {
  referenceString: string | undefined
}): JSX.Element {
  const debouncedSubmit = useDebouncedCallback(onSubmit, 2500) // We debounce to avoid adding too many entries to the version history. Since we already flush on blur, it seemed like 2.5 seconds was a good debounce interval.

  // If referenceString changes, we want to flush the debounced submit.
  useEffect(
    () => () => {
      debouncedSubmit.flush()
    },
    [referenceString, debouncedSubmit]
  )

  const typedRender = render as FieldFormProps<TValue>['render']
  const flush = debouncedSubmit.flush
  const renderWithOnBlur = useCallback<FieldFormProps<TValue>['render']>(
    ({ field: { onBlur: fieldOnBlur, ...fieldRest }, fieldState, formState }) =>
      typedRender({
        field: {
          onBlur: () => {
            fieldOnBlur()
            flush()
          },
          ...fieldRest,
        },
        fieldState,
        formState,
      }),
    [typedRender, flush]
  )
  return (
    <FieldForm
      render={renderWithOnBlur}
      onSubmit={debouncedSubmit}
      {...delegated}
    />
  )
}
